import { useQuery } from '@tanstack/react-query';

import { api } from 'api/v1/api';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';

export type TutorTrustScoreEvent = {
  id: number;
  event_type: string;
  delta: number;
  trust_score_after: number;
  trust_level_after: string;
  reason_ru?: string | null;
  meta?: Record<string, unknown>;
  created_at?: string | null;
};

export type TutorTrustScoreQuest = {
  code: string;
  title_ru: string;
  description_ru?: string | null;
  delta: number;
  progress: number;
  target: number;
  metric?: string | null;
  status: 'available' | 'in_progress' | 'completed' | 'expired' | string;
  expires_at?: string | null;
  subject_label?: string | null;
};

export type TutorTrustScoreChecklistItem = {
  code: string;
  field?: string | null;
  title_ru: string;
  delta: number;
  status: 'done' | 'pending_moderation' | 'ready' | 'missing' | string;
  awarded: boolean;
  hint_ru?: string | null;
  group?: 'profile' | 'verification' | 'bonus' | string;
  group_title_ru?: string;
  href?: string | null;
  status_label_ru?: string | null;
};

export type TutorTrustScoreSummary = {
  tutor_profile_id: number;
  trust_score: number;
  trust_level: 'bronze' | 'silver' | 'gold' | 'platinum' | string;
  stored_trust_level?: string;
  passport_verified?: boolean;
  profile_photo_present?: boolean;
  level_cap_reasons?: string[];
  level_cap_reasons_ru?: string[];
  onboarding_checklist?: TutorTrustScoreChecklistItem[];
  review_avg: number;
  progress: {
    current_level?: string;
    next_level?: string | null;
    score?: number;
    threshold?: number;
    next_threshold?: number | null;
    progress?: number;
    remaining?: number;
  };
  perks: {
    max_daily_responses?: number;
    allowed_tiers?: string[];
    distribution_wave?: number;
  };
  daily_responses?: {
    date: string;
    responses_used: number;
    responses_max: number;
    responses_remaining: number;
    resets_at: string;
  };
  recent_events?: TutorTrustScoreEvent[];
  quests?: TutorTrustScoreQuest[];
};

async function fetchTutorTrustScore(): Promise<TutorTrustScoreSummary> {
  const response = await api.get<TutorTrustScoreSummary>('/tutors/me/trust-score');
  return response.data;
}

export function useTutorTrustScore() {
  const { data: session } = useAuthSession();

  return useQuery({
    queryKey: ['tutor-trust-score', 'me'],
    queryFn: fetchTutorTrustScore,
    enabled: isAuthSessionActive(session),
    staleTime: 60_000,
    retry: false,
  });
}
